import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import AppHistory from '__common/modules/history';
import { dispatch } from '__common/store';
import { fetchOsDesignConfigInitDataRequest } from 'actions';
import { USER_LOGIN_SUCCESS } from 'actions';
import { Observable } from 'rxjs';
import { hitApi } from '__common/utils/api';
import { parseDataForBomSave } from 'bom/bomHelpers';
import products from '__common/utils/products';

type Props = {
  dispatch: Function;
  isOsImport: boolean;
  msg: string;
  match: any;
};

const OpenSolarImport: React.FC<Props> = ({ msg }) => {
  const [payload, setPayload] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Function to authenticate with UBuilder using app credentials
  const authenticateWithUbuilder = async (appId: string, secretKeyJWT: string) => {
    try {
      const req = hitApi('post', 'api/auth/ubuilder-app-login/', { app_id: appId, secret_key_jwt: secretKeyJWT });
      const { response } = await Observable.ajax(req).toPromise();
      if (response.token) {
        const authData = await response;
        return authData.token;
      } else {
        console.error('UBuilder authentication failed');
        return null;
      }
    } catch (error) {
      console.error('Error authenticating with UBuilder:', error);
      return null;
    }
  };

  useEffect(() => {
    console.log('OpenSolarImport mounted');
    
    if (window.parent !== window) {
      console.log('Sending UBUILDER_READY to parent');
      window.parent.postMessage({ type: 'UBUILDER_READY' }, '*');
    } else {
      console.log('Not in an iframe, not sending readiness signal');
    }

    const handler = async (event: MessageEvent) => {
      console.log('Received message:', event.data);
      
      if (event.data && event.data.type === 'OPENSOLAR_PROJECT_DATA') {
        const originalPayload = event.data.payload;
        // console.log('Received OpenSolar payload:', originalPayload);
        
        // Extract UBuilder credentials
        const ubuilderAppId = "open-solar-app";
        const ubuilderSecretKeyJWT = originalPayload.jwt;
        
        if (ubuilderAppId && ubuilderSecretKeyJWT) {
          console.log('Authenticating with UBuilder...');
          
          // Authenticate with UBuilder to get token
          const authToken = await authenticateWithUbuilder(ubuilderAppId, ubuilderSecretKeyJWT);
          
          if (authToken) {
            console.log('UBuilder authentication successful, token received');
            
            // Set the auth token in Redux state
            dispatch(USER_LOGIN_SUCCESS(authToken, 'open-solar', 'opensolar@testing.com', false, false, [], []));
            
            // Update payload with the generated token
            const updatedPayload = {
              ...originalPayload,
              authToken: authToken,
              is_os_project: true
            };
            
            setPayload(updatedPayload);
            setLoading(false);
            
            // Dispatch the action with updated payload
            dispatch(fetchOsDesignConfigInitDataRequest(updatedPayload));
            
            // Navigate to config page with updated payload
            AppHistory.push({ 
              pathname: '/opensolar_design', 
              state: { payload: updatedPayload } 
            });
          } else {
            console.error('Failed to authenticate with UBuilder');
            window.parent.postMessage({ type: 'UBUILDER_AUTHENTICATION_FAILED' }, '*');
            setLoading(false);
          }
        } else {
          console.error('Missing UBuilder credentials in payload');
          window.parent.postMessage({ type: 'UBUILDER_AUTHENTICATION_FAILED' }, '*');
          setLoading(false);
        }
      }
    };
    
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Keep a minimal loader view in case we render briefly before redirect
  return (
    <div className={`loader-svg-container`}>
      <svg className="loader-svg-machine" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 125 390 395">
        <defs/>
        <g fill="freeze" >
          <path fill="freeze" x="-136,996" y="-136,996" className="loader-svg-medium-shadow" d="M402 400v-21l-28-4c-1-10-4-19-7-28l23-17 -11-18L352 323c-6-8-13-14-20-20l11-26 -18-11 -17 23c-9-4-18-6-28-7l-4-28h-21l-4 28c-10 1-19 4-28 7l-17-23 -18 11 11 26c-8 6-14 13-20 20l-26-11 -11 18 23 17c-4 9-6 18-7 28l-28 4v21l28 4c1 10 4 19 7 28l-23 17 11 18 26-11c6 8 13 14 20 20l-11 26 18 11 17-23c9 4 18 6 28 7l4 28h21l4-28c10-1 19-4 28-7l17 23 18-11 -11-26c8-6 14-13 20-20l26 11 11-18 -23-17c4-9 6-18 7-28L402 400zM265 463c-41 0-74-33-74-74 0-41 33-74 74-74 41 0 74 33 74 74C338 430 305 463 265 463z"/>
        </g>
        <g fill="freeze" >
          <path  fill="freeze" x="-100,136" y="-100,136" className="loader-svg-small-shadow" d="M210 246v-21l-29-4c-2-10-6-18-11-26l18-23 -15-15 -23 18c-8-5-17-9-26-11l-4-29H100l-4 29c-10 2-18 6-26 11l-23-18 -15 15 18 23c-5 8-9 17-11 26L10 225v21l29 4c2 10 6 18 11 26l-18 23 15 15 23-18c8 5 17 9 26 11l4 29h21l4-29c10-2 18-6 26-11l23 18 15-15 -18-23c5-8 9-17 11-26L210 246zM110 272c-20 0-37-17-37-37s17-37 37-37c20 0 37 17 37 37S131 272 110 272z"/>
        </g>
        <g fill="freeze" >
          <path  fill="freeze" x="-100,136" y="-100,136" className="loader-svg-small" d="M200 236v-21l-29-4c-2-10-6-18-11-26l18-23 -15-15 -23 18c-8-5-17-9-26-11l-4-29H90l-4 29c-10 2-18 6-26 11l-23-18 -15 15 18 23c-5 8-9 17-11 26L0 215v21l29 4c2 10 6 18 11 26l-18 23 15 15 23-18c8 5 17 9 26 11l4 29h21l-4-29c10-2 18-6 26-11l23 18 15-15 -18-23c5-8 9-17 11-26L200 236zM100 262c-20 0-37-17-37-37s17-37 37-37c20 0 37 17 37 37S121 262 100 262z"/>
        </g>
        <g fill="freeze" >
          <path  fill="freeze" x="-136,996" y="-136,996" className="loader-svg-medium" d="M392 390v-21l-28-4c-1-10-4-19-7-28l23-17 -11-18L342 313c-6-8-13-14-20-20l11-26 -18-11 -17 23c-9-4-18-6-28-7l-4-28h-21l-4 28c-10 1-19 4-28 7l-17-23 -18 11 11 26c-8 6-14 13-20 20l-26-11 -11 18 23 17c-4 9-6 18-7 28l-28 4v21l28 4c1 10 4 19 7 28l-23 17 11 18 26-11c6 8 13 14 20 20l-11 26 18 11 17-23c9 4 18 6 28 7l4 28h21l4-28c10-1 19-4 28-7l17 23 18-11 -11-26c8-6 14-13 20-20l26 11 11-18 -23-17c4-9 6-18 7-28L392 390zM255 453c-41 0-74-33-74-74 0-41 33-74 74-74 41 0 74 33 74 74C328 420 295 453 255 453z"/>
        </g>
      </svg>
      <div className="loading-text">Authenticating with UBuilder...</div>
    </div>
  );
};

function mapStateToProps(appState: appState) {
  return {
    isOsImport: appState.app.isOsImport,
    msg: appState.app.loaderMsg,
  };
}

export default connect(mapStateToProps)(OpenSolarImport);